import { RefObject } from 'react';
import { ArrowUpwardRounded, UploadFileRounded, DeleteOutlineRounded } from '@mui/icons-material';
import { SparkMark, SparkSvg } from './icons';
import { SLASH_COMMANDS } from './constants';
import { IMode, FormType, FORM_TYPE_LABELS, IDocument } from './types';
import AIDisclaimer from '../ai-disclaimer/ai-disclaimer';

const FORM_TYPES: FormType[] = ['sales_invoice', 'purchase_invoice'];

interface Props {
	inputValue: string;
	isTyping: boolean;
	mode: IMode;
	formType: FormType | null;
	showSlashMenu: boolean;
	filteredCommands: typeof SLASH_COMMANDS;
	slashIndex: number;
	textareaRef: RefObject<HTMLTextAreaElement>;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onKeyDown: (e: React.KeyboardEvent) => void;
	onSend: () => void;
	onSlashHover: (idx: number) => void;
	onSlashSelect: (id: IMode) => void;
	onModeChange: (id: IMode) => void;
	onFormTypeChange: (ft: FormType) => void;
	activeDocument?: IDocument | null;
	activeDocuments?: IDocument[];
	onDocDelete?: () => void;
	onDocUpload?: () => void;
	onMultiDocDelete?: (docId?: string) => void;
}

const ChatBotInput = ({
	inputValue,
	isTyping,
	mode,
	formType,
	showSlashMenu,
	filteredCommands,
	slashIndex,
	textareaRef,
	onChange,
	onKeyDown,
	onSend,
	onSlashHover,
	onSlashSelect,
	onModeChange,
	onFormTypeChange,
	activeDocument,
	activeDocuments,
	onDocDelete,
	onDocUpload,
	onMultiDocDelete,
}: Props) => {
	const canSend = mode === 'agent'
		? (formType !== null && !isTyping)
		: mode === 'doc_qa'
			? (activeDocuments && activeDocuments.length > 0)
				? (activeDocuments.every(d => d.status === 'ready') && !!inputValue.trim() && !isTyping)
				: (activeDocument?.status === 'ready' && !!inputValue.trim() && !isTyping)
			: (!!inputValue.trim() && !isTyping);

	return (
		<div className='chatBot--input-area'>
			{showSlashMenu && filteredCommands.length > 0 && (
				<div className='chatBot--slash-menu'>
					<div className='chatBot--slash-menu-label'>Commands</div>
					{filteredCommands.map((cmd, idx) => (
						<button
							key={cmd.id}
							className={`chatBot--slash-item${idx === slashIndex ? ' chatBot--slash-item--active' : ''}`}
							onMouseEnter={() => onSlashHover(idx)}
							onClick={() => onSlashSelect(cmd.id)}>
							<SparkMark size={28} />
							<div>
								<div className='chatBot--slash-item-label'>{cmd.label}</div>
								<div className='chatBot--slash-item-desc'>{cmd.description}</div>
							</div>
						</button>
					))}
				</div>
			)}

			{mode === 'agent' && (
				<div className='chatBot--form-type-bar'>
					<span className='chatBot--form-type-label'>Form type</span>
					<div className='chatBot--form-type-options'>
						{FORM_TYPES.map((ft) => (
							<button
								key={ft}
								className={`chatBot--form-type-btn${formType === ft ? ' chatBot--form-type-btn--active' : ''}`}
								onClick={() => onFormTypeChange(ft)}>
								{FORM_TYPE_LABELS[ft]}
							</button>
						))}
					</div>
				</div>
			)}

			{mode === 'doc_qa' && activeDocument && (!activeDocuments || activeDocuments.length === 0) && (
				<div className='chatBot--doc-chip-bar'>
					<div className='chatBot--doc-chip'>
						<span className='chatBot--doc-chip-dot' style={{
							background: activeDocument.status === 'ready' ? '#2EB273'
								: activeDocument.status === 'failed' ? '#C62828' : '#2EB273'
						}} />
						<span className='chatBot--doc-chip-name'>{activeDocument.filename}</span>
						{activeDocument.status === 'ready' && (
							<span className='chatBot--doc-chip-badge'>Ready</span>
						)}
					</div>
					{/* <button className='chatBot--doc-chip-action' onClick={onDocUpload} title='Upload different file'>
						<UploadFileRounded sx={{ fontSize: 15 }} />
					</button>
					<button className='chatBot--doc-chip-action chatBot--doc-chip-action--danger' onClick={onDocDelete} title='Remove document'>
						<DeleteOutlineRounded sx={{ fontSize: 15 }} />
					</button> */}
				</div>
			)}

			{mode === 'doc_qa' && activeDocuments && activeDocuments.length > 0 && (
				<div className='chatBot--doc-chip-bar'>
					{activeDocuments.map(doc => (
						<div key={doc.doc_id} className='chatBot--doc-chip'>
							<span className='chatBot--doc-chip-dot' style={{
								background: doc.status === 'ready' ? '#2EB273'
									: doc.status === 'failed' ? '#C62828' : '#2EB273'
							}} />
							<span className='chatBot--doc-chip-name' title={doc.filename}>{doc.filename}</span>
							{doc.status === 'ready' && (
								<span className='chatBot--doc-chip-badge'>Ready</span>
							)}
							<button className='chatBot--doc-chip-action chatBot--doc-chip-action--danger' onClick={() => onMultiDocDelete?.(doc.doc_id)} title='Remove document'>
								<DeleteOutlineRounded sx={{ fontSize: 15 }} />
							</button>
						</div>
					))}
					{/* <button className='chatBot--doc-chip-action' onClick={onDocUpload} title='Upload more files'>
						<UploadFileRounded sx={{ fontSize: 15 }} />
					</button>
					{activeDocuments.length > 1 && (
						<button className='chatBot--doc-chip-action chatBot--doc-chip-action--danger' onClick={() => onMultiDocDelete?.()} title='Remove all documents'>
							<DeleteOutlineRounded sx={{ fontSize: 15 }} />
						</button>
					)} */}
				</div>
			)}

			<div className='chatBot--input-wrap'>
				<textarea
					ref={textareaRef}
					className='chatBot--textarea'
					rows={2}
					placeholder={
						mode === 'doc_qa'
							? (activeDocuments && activeDocuments.length > 0)
								? activeDocuments.every(d => d.status === 'ready')
									? 'Ask a question about the document(s)…'
									: 'Documents are processing…'
								: activeDocument?.status === 'ready'
									? 'Ask a question about this document…'
									: activeDocument
										? 'Document is still processing…'
										: 'Upload PDF(s) above, then ask questions…'
							: mode === 'agent'
								? 'Add any context for the agent…'
								: 'Ask anything, or describe an action…'
					}
					value={inputValue}
					onChange={onChange}
					onKeyDown={onKeyDown}
					disabled={
						isTyping ||
						(mode === 'doc_qa' && !!activeDocument && activeDocument.status !== 'ready') ||
						(mode === 'doc_qa' && !!activeDocuments && activeDocuments.length > 0 && activeDocuments.some(d => d.status !== 'ready'))
					}
				/>
				<div className='chatBot--ctrls'>
					<div className='chatBot--mode-tabs'>
						{SLASH_COMMANDS.map((cmd) => (
							<button
								key={cmd.id}
								className={`chatBot--mode-tab${mode === cmd.id ? ' chatBot--mode-tab--active' : ''}`}
								onClick={() => onModeChange(cmd.id)}
								title={cmd.description}>
								{mode === cmd.id && <SparkSvg size={11} />}
								{cmd.label}
							</button>
						))}
					</div>
					<span className='chatBot--ctrls-grow' />
					<button
						className={`chatBot--send-btn${canSend ? ' active' : ''}`}
						onClick={onSend}
						disabled={!canSend}
						title='Send'>
						<ArrowUpwardRounded style={{ fontSize: 15 }} />
					</button>
				</div>
			</div>

			<div className='chatBot--hints' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', overflow: 'hidden' }}>
				<div style={{ color: '#9ca3af', fontSize: '10px', whiteSpace: 'nowrap' }}>
					{mode === 'doc_qa' ? 'Document Q&A mode' : 'Replies use ERP data only'}
				</div>
				<div style={{ width: '1px', height: '10px', background: '#d1d5db', flexShrink: 0 }} />
				<div style={{ marginTop: '-16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
					<AIDisclaimer showIcon={false} justifyContent="flex-start" />
				</div>
			</div>
		</div>
	);
};

export default ChatBotInput;
